/**
 * Arrêt d'urgence de l'IA — règles PURES (§ 4.10.1, DIA3-24, ARCHI-2-03 ; planche
 * `ArretUrgenceIA`). Un seul composant, deux portées : plateforme et organisation. L'IA est
 * arrêtée pour un membre si la bascule plateforme OU celle de son organisation est active ; le
 * bandeau nomme CHAQUE cause active. La passerelle reste seule juge (`AiModelAccessGuard`) :
 * ces fonctions ne décident de rien, elles disent ce que le serveur a répondu.
 */
import type { AiKillSwitchHistoryRow } from '@/api/types/aiAdmin'

export type Portee = 'plateforme' | 'organisation'

/** État d'une portée tel que lu sur la passerelle. */
export interface EtatPortee {
  enabled: boolean
  reason?: string | null | undefined
  updated_at?: string | null | undefined
  updated_by?: string | null | undefined
}

export interface CauseArret {
  portee: Portee
  motif: string | null
  depuis: string | null
  par: string | null
}

/** Ce que l'arrêt interrompt, et ce qui reste possible (planche `ArretUrgenceIA`). */
export const INTERROMPU = [
  'générations (cadrage, spécification, backlog, application)',
  'missions de la colonie : suspendues à l’étape en cours',
  'chat et interrogation documentaire',
] as const
export const RESTE_POSSIBLE = [
  'arrêter un aperçu',
  'revenir en arrière (retour arrière)',
  'déployer une version déjà construite',
  'exécuter les campagnes de tests et les pipelines',
] as const
export const RESTENT_POSSIBLES_PHRASE =
  'Restent possibles : arrêter un aperçu, revenir en arrière, déployer, exécuter les campagnes de tests et les pipelines.'

/** Nom à taper pour confirmer la bascule de la portée plateforme (DEC-052, vérifié serveur). */
export const NOM_PLATEFORME = 'plateforme'

/**
 * État de la portée plateforme déduit de l'historique de l'organisation : la passerelle y
 * joint les bascules de la plateforme (elles arrêtent aussi l'organisation). Sert au
 * propriétaire, qui ne lit pas `/admin/ai/platform/kill-switch` (réservé au super-admin).
 * L'historique arrive du plus récent au plus ancien ; `undefined` si aucune bascule connue.
 */
export function etatPlateformeDepuisHistorique(lignes: readonly AiKillSwitchHistoryRow[]): EtatPortee | undefined {
  const derniere = [...lignes]
    .filter((l) => l.scope === 'global')
    .sort((a, b) => (a.changed_at < b.changed_at ? 1 : a.changed_at > b.changed_at ? -1 : b.id - a.id))[0]
  if (!derniere) return undefined
  return { enabled: derniere.enabled, reason: derniere.reason, updated_at: derniere.changed_at, updated_by: derniere.changed_by }
}

/** Organisations encore arrêtées par LEUR propre bascule (vue plateforme, § 4.10.1). */
export function organisationsArreteesParElles(lignes: readonly AiKillSwitchHistoryRow[]): string[] {
  const derniere = new Map<string, AiKillSwitchHistoryRow>()
  for (const l of lignes) {
    if (l.scope !== 'organisation' || !l.organisation_id) continue
    const connue = derniere.get(l.organisation_id)
    if (!connue || l.changed_at > connue.changed_at || (l.changed_at === connue.changed_at && l.id > connue.id)) {
      derniere.set(l.organisation_id, l)
    }
  }
  return [...derniere.values()].filter((l) => l.enabled).map((l) => l.organisation_id!).sort()
}

/** Les causes actives, plateforme d'abord (règle OU, AC-PF7). */
export function composerCauses({ plateforme, organisation }: {
  plateforme?: EtatPortee | undefined
  organisation?: EtatPortee | undefined
}): CauseArret[] {
  const causes: CauseArret[] = []
  const cause = (portee: Portee, e: EtatPortee): CauseArret => ({
    portee, motif: e.reason?.trim() ? e.reason.trim() : null, depuis: e.updated_at ?? null, par: e.updated_by ?? null,
  })
  if (plateforme?.enabled) causes.push(cause('plateforme', plateforme))
  if (organisation?.enabled) causes.push(cause('organisation', organisation))
  return causes
}

/** « 26/09 14:05 » ; la valeur brute si elle n'est pas une date lisible. */
export function formaterInstant(iso: string | null | undefined): string | null {
  if (!iso) return null
  // `timestamptz::text` de PostgreSQL rend « 2026-10-01 09:34:52.5+00 » : l'espace et le
  // décalage sans minutes ne sont pas de l'ISO 8601 que `Date` accepte partout (lot PL).
  const normalise = (iso.includes('T') || iso.endsWith('Z') ? iso : iso.replace(' ', 'T')).replace(/([+-]\d\d)$/, '$1:00')
  const d = new Date(normalise)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Titre d'une cause, au mot près de la planche. */
export function titreCause(cause: CauseArret, organisation: string | undefined): string {
  return cause.portee === 'plateforme'
    ? 'Requêtes IA interrompues sur toute la plateforme'
    : `Requêtes IA interrompues dans ${organisation ?? 'votre organisation'}`
}

/** Détail d'une cause : motif, depuis, par. */
export function detailCause(cause: CauseArret): string {
  const morceaux: string[] = []
  if (cause.motif) morceaux.push(`Motif : « ${cause.motif} »`)
  const depuis = formaterInstant(cause.depuis)
  if (depuis) morceaux.push(`depuis ${depuis}`)
  if (cause.par) morceaux.push(`par ${cause.par}`)
  return morceaux.length > 0 ? `${morceaux.join(' · ')}.` : ''
}

/** Événement émis quand la passerelle refuse un appel pour arrêt d'urgence (503
 *  `AI_KILL_SWITCH_ACTIVE`) : le bandeau s'affiche même pour qui ne peut pas lire la cause. */
export const EVENEMENT_ARRET_CONSTATE = 'doit:arret-ia-constate'

export function signalerArretConstate(): void {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENEMENT_ARRET_CONSTATE))
  } catch { /* environnement sans DOM : rien à signaler */ }
}
